import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Code,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';

import {
  IApiRecurso,
  IApiToken,
  IApiTokenRevelado,
  useApiController,
} from '../../../hooks/useApiController';
import { IDataNoPagination } from '../../../types/shared';
import { CopyButton } from './CopyButton';
import { gerarSnippetCurl, gerarSnippetPython } from './gerarSnippet';

interface Props {
  token: IApiToken | null;
  onClose: () => void;
}

// Revela (decifra) a API key + HMAC secret de um token existente, p/ reenviar ao cliente.
// Só busca quando um token é selecionado. O backend registra no access log quem revelou.
// Além das credenciais, remonta o exemplo de chamada (curl/Python) — o mesmo da criação.
export function RevelarTokenModal({ token, onClose }: Props) {
  const { revelarToken, getRecursos } = useApiController();

  const { data, isFetching, isError } = useQuery<IDataNoPagination<IApiTokenRevelado>>({
    queryKey: ['api-controller-revelar', token?.id],
    queryFn: () => revelarToken(token!.id),
    enabled: !!token,
  });

  // Rota (recurso) vinculada ao token → o slug dela é o que compõe o path do exemplo
  // (`/external/v1/<slug>/`). Enquanto os recursos não chegam, ou se a rota do token não
  // estiver mais na lista, fica undefined e o snippet estampa o placeholder explícito.
  const { data: recursosData } = useQuery<IDataNoPagination<IApiRecurso[]>>({
    queryKey: ['api-controller-recursos'],
    queryFn: getRecursos,
    enabled: !!token,
  });
  const rotaSlug = useMemo(
    () => (recursosData?.data ?? []).find(r => r.id === token?.api_recurso_id)?.slug,
    [recursosData, token],
  );

  const revelado = data?.data;

  const renderConteudo = () => {
    if (isFetching) {
      return (
        <Flex justify="center" py={8}>
          <Spinner />
        </Flex>
      );
    }
    if (isError) {
      return <Text color="red.500">Não foi possível revelar as credenciais.</Text>;
    }
    return (
      <Box display="flex" flexDirection="column" gap={3}>
        {revelado?.api_key ? (
          <FormControl>
            <FormLabel mb={1}>API key</FormLabel>
            <Flex gap={2}>
              <Code p={2} flex={1} overflowX="auto" whiteSpace="nowrap">
                {revelado.api_key}
              </Code>
              <CopyButton value={revelado.api_key} />
            </Flex>
          </FormControl>
        ) : (
          <Box bg="yellow.50" p={3} borderRadius="md">
            <Text fontSize="sm" color="yellow.800">
              Este token foi criado antes do armazenamento recuperável — a API key não pode ser
              exibida. Crie um novo token para obter uma key.
            </Text>
          </Box>
        )}

        {revelado?.hmac_secret && (
          <FormControl>
            <FormLabel mb={1}>HMAC secret</FormLabel>
            <Flex gap={2}>
              <Code p={2} flex={1} overflowX="auto" whiteSpace="nowrap">
                {revelado.hmac_secret}
              </Code>
              <CopyButton value={revelado.hmac_secret} />
            </Flex>
          </FormControl>
        )}

        {/* Exemplo de chamada — só faz sentido com api_key (senão o cliente não autentica) */}
        {revelado?.api_key && revelado?.hmac_secret && (
          <>
            <Divider my={1} />
            <FormLabel mb={0}>Exemplo de chamada (envie ao cliente)</FormLabel>
            <Tabs size="sm" variant="enclosed">
              <TabList>
                <Tab>cURL</Tab>
                <Tab>Python</Tab>
              </TabList>
              <TabPanels>
                {[
                  {
                    lang: 'curl',
                    code: gerarSnippetCurl({
                      apiKey: revelado.api_key,
                      hmacSecret: revelado.hmac_secret,
                      rotaSlug,
                      baseUrl: revelado.base_url,
                    }),
                  },
                  {
                    lang: 'python',
                    code: gerarSnippetPython({
                      apiKey: revelado.api_key,
                      hmacSecret: revelado.hmac_secret,
                      rotaSlug,
                      baseUrl: revelado.base_url,
                    }),
                  },
                ].map(({ lang, code }) => (
                  <TabPanel key={lang} px={0}>
                    <Flex justify="flex-end" mb={1}>
                      <CopyButton value={code} label="Copiar código" />
                    </Flex>
                    <Code
                      display="block"
                      whiteSpace="pre"
                      overflowX="auto"
                      p={3}
                      borderRadius="md"
                      fontSize="xs"
                    >
                      {code}
                    </Code>
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </>
        )}
      </Box>
    );
  };

  return (
    <Modal isOpen={!!token} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>Credenciais do token{token ? ` — ${token.nome}` : ''}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>{renderConteudo()}</ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Fechar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
